import { useCallback, useEffect, useState } from 'react';

/** The four values a generation run is laid out against. */
export interface BuilderSpacing {
  wellSpacingFt: number | null;
  crossLineSetbackFt: number | null;
  inLineSetbackFt: number | null;
  spacingToleranceFt: number | null;
}

export interface BuilderSpacingState extends BuilderSpacing {
  set: (patch: Partial<BuilderSpacing>) => void;
  /** Treat the unit as greenfield, ignoring wells already drilled in it. */
  ignoreExistingDevelopment: boolean;
  setIgnoreExistingDevelopment: (value: boolean) => void;
  /** Derived from well spacing — the mock shows it beside the input. */
  wellsPerMile: number | null;
}

const FT_PER_MILE = 5280;

/**
 * Spacing and setback inputs for the builder, plus the greenfield toggle.
 *
 * Held above both panes because a generation run reads them while the gun barrel
 * draws against them, and the mock keeps them in the same one place.
 *
 * @param {BuilderSpacing} seed - Values from the scoped unit's inventory details.
 * @return {BuilderSpacingState} Current values and setters.
 */
export function useBuilderSpacing(seed: BuilderSpacing): BuilderSpacingState {
  const [values, setValues] = useState<BuilderSpacing>(seed);
  const [ignoreExistingDevelopment, setIgnoreExistingDevelopment] =
    useState(false);

  // Reseed when the scoped unit's stored spacing arrives or changes; local edits
  // on a previous unit are not carried across.
  useEffect(() => {
    setValues(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    seed.wellSpacingFt,
    seed.crossLineSetbackFt,
    seed.inLineSetbackFt,
    seed.spacingToleranceFt,
  ]);

  const set = useCallback((patch: Partial<BuilderSpacing>) => {
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const wellsPerMile =
    values.wellSpacingFt && values.wellSpacingFt > 0
      ? Math.round(FT_PER_MILE / values.wellSpacingFt)
      : null;

  return {
    ...values,
    set,
    ignoreExistingDevelopment,
    setIgnoreExistingDevelopment,
    wellsPerMile,
  };
}
